import React, { useMemo, useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    Tabs,
    Tab,
    Button,
    Stack,
    Divider,
    IconButton,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useGetOrderNotes } from '../../../hooks/orders/useGetOrderNotes';
import { useCreateOrderNote } from '../../../hooks/orders/useCreateOrderNote';
import { useDeleteOrderNote } from '../../../hooks/orders/useDeleteOrderNote';
import { useAuthContext } from '../../../auth/useAuthContext';
import { usePermissions } from '../../../auth/PermissionsContext';
import FormInput from '../../inputs/FormInput';

const OrderNotes = ({ orderId }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [tab, setTab] = useState('internal');
    const [body, setBody] = useState('');

    const { user } = useAuthContext() || {};
    const { has } = usePermissions() || { has: () => false };
    const canManageAnyNote = has?.('orders.manage_any_note');

    const { notes, isLoading } = useGetOrderNotes(orderId);
    const { createOrderNoteMutate, isCreatingNote } = useCreateOrderNote();
    const { deleteOrderNoteMutate, isDeletingNote } = useDeleteOrderNote();

    const filteredNotes = useMemo(
        () => notes.filter((n) => (tab === 'all' ? true : n.visibility === tab)),
        [notes, tab]
    );

    const handleSubmit = () => {
        if (!body.trim()) return;
        createOrderNoteMutate(
            { id: orderId, data: { body, visibility: tab } },
            { onSuccess: () => setBody('') }
        );
    };

    const handleDelete = (noteId) => {
        deleteOrderNoteMutate({ id: orderId, noteId });
    };

    const canDelete = (note) =>
        canManageAnyNote || note.author_id === user?.id || note.author?.id === user?.id;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={1}>
                {isArabic ? 'الملاحظات' : 'Notes'}
            </Typography>

            <Tabs
                value={tab}
                onChange={(_e, v) => setTab(v)}
                indicatorColor="primary"
                textColor="primary"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab value="internal" label={isArabic ? 'داخلية' : 'Internal'} />
                <Tab value="customer" label={isArabic ? 'للعميل' : 'Customer-visible'} />
            </Tabs>

            <Box sx={{ mb: 2 }}>
                <FormInput
                    type="textarea"
                    name="note_body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={
                        tab === 'internal'
                            ? (isArabic ? 'ملاحظة داخلية...' : 'Internal note...')
                            : (isArabic ? 'ملاحظة يراها العميل...' : 'Customer-visible note...')
                    }
                    required={false}
                    textareaRows="80px"
                />
                <Stack direction="row" justifyContent="flex-end" mt={1}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSubmit}
                        disabled={!body.trim() || isCreatingNote}
                    >
                        {isCreatingNote
                            ? (isArabic ? 'جاري...' : 'Adding...')
                            : (isArabic ? 'إضافة' : 'Add')}
                    </Button>
                </Stack>
            </Box>

            <Divider />

            <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mt: 1.5 }}>
                {isLoading ? (
                    <Typography variant="body2" color="text.secondary">
                        {isArabic ? 'جاري التحميل...' : 'Loading...'}
                    </Typography>
                ) : filteredNotes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {isArabic ? 'لا توجد ملاحظات' : 'No notes yet'}
                    </Typography>
                ) : (
                    filteredNotes.map((note) => (
                        <Box key={note.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">{note.body}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {note.author?.name || (note.author_type === 'system' ? 'system' : '-')}
                                    {' • '}
                                    {dayjs(note.created_at).format('YYYY-MM-DD HH:mm')}
                                </Typography>
                            </Box>
                            {canDelete(note) && (
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(note.id)}
                                    disabled={isDeletingNote}
                                >
                                    <DeleteOutline fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    ))
                )}
            </Stack>
        </Paper>
    );
};

export default OrderNotes;
